import { useEffect, useRef } from 'react'

// The ad script pins a sticky anchor ad to the bottom of the viewport. scrollIntoView() doesn't
// know about that fixed overlay, so it parks an element right at the viewport bottom — behind the
// ad. So instead we scroll an element so its bottom rests this many px ABOVE the viewport bottom,
// clearing the ad. Approximate (anchor-ad heights vary); errs on the generous side.
const STICKY_AD_CLEARANCE = 150

// Scroll `el` DOWN just enough that its bottom sits STICKY_AD_CLEARANCE above the viewport bottom
// (so its button clears the sticky ad), and no further. Only scrolls when the element's bottom is
// below that line — so it never over-scrolls or needlessly pushes the puzzle off the top.
export function scrollClearingStickyAd(el: HTMLElement) {
  const restLine = window.innerHeight - STICKY_AD_CLEARANCE
  const delta = el.getBoundingClientRect().bottom - restLine
  if (delta > 4) window.scrollBy({ top: delta, behavior: 'smooth' })
}

// Scroll now, AND re-scroll whenever the page height changes over the next ~1.2s. The rebus picture
// canvas paints asynchronously (awaits fonts + icon fetches) and only then jumps to full size,
// pushing the button down AFTER a one-shot scroll has already measured — so the button lands behind
// the ad. A ResizeObserver on <body> catches that late height jump and re-runs the scroll against
// the settled layout. Self-disconnects after a short window (no more late shifts expected). Returns
// a cleanup to stop early on unmount / phase change. Harmless where nothing resizes (e.g. cryptic).
export function scrollClearingStickyAdSettling(el: HTMLElement | null): () => void {
  if (el) scrollClearingStickyAd(el)
  if (!el || typeof ResizeObserver === 'undefined') return () => {}
  let stopped = false
  const ro = new ResizeObserver(() => {
    if (!stopped && document.body.contains(el)) scrollClearingStickyAd(el)
  })
  ro.observe(document.body)
  const timer = window.setTimeout(() => {
    stopped = true
    ro.disconnect()
  }, 1200)
  return () => {
    stopped = true
    window.clearTimeout(timer)
    ro.disconnect()
  }
}

// Reveal a just-appeared control (retro / reflection) on mount, clearing the sticky ad and coping
// with the async picture resize. Attach the returned ref to the section root (its bottom is the
// primary button).
export function useScrollIntoViewOnMount<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => scrollClearingStickyAdSettling(ref.current), [])
  return ref
}
